<template>
  <section class="mt-8">
    <h3 class="text-xl font-semibold text-gray-700 mb-1">Rated Messages</h3>
    <p class="text-sm text-gray-500 mb-4">Reviewed messages of the selected user for this sprint.</p>

    <div v-if="loading" class="rounded-xl border border-indigo-100 bg-indigo-50 px-4 py-6 text-center text-indigo-700">
      Loading rated messages…
    </div>
    <div v-else-if="error" class="rounded-xl border border-red-100 bg-red-50 px-4 py-4 text-red-700">
      {{ error }}
    </div>
    <template v-else>
      <div v-if="averages.length" class="overflow-x-auto rounded-xl border border-gray-100 mb-6">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Criterion</th>
              <th class="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">User Average</th>
              <th class="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Ratings</th>
              <th class="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide" title="Only includes users with the same role in this session">Role Average</th>
                            <th class="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide" title="Only includes users with the same role in this session">Role Ratings</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-100">
            <tr v-for="c in averages" :key="c.criterion_id">
              <td class="px-4 py-3 text-sm text-gray-800">{{ c.criterion_name }}</td>
              <td class="px-4 py-3 text-sm font-semibold text-gray-900">{{ formatAverage(c.user_average) }}</td>
              <td class="px-4 py-3 text-sm text-gray-700">{{ c.user_ratings || 0 }}</td>
              <td class="px-4 py-3 text-sm font-semibold text-gray-900">{{ formatAverage(c.overall_average) }}</td>
              <td class="px-4 py-3 text-sm text-gray-700">{{ c.overall_ratings || 0 }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <button
        type="button"
        class="flex items-center gap-2 w-full text-left mb-3"
        :aria-expanded="expanded"
        @click="expanded = !expanded"
      >
        <ChevronDownIcon class="h-5 w-5 text-gray-500 transition-transform" :class="{ '-rotate-90': !expanded }" />
        <span class="text-lg font-semibold text-gray-700">Messages</span>
        <span class="text-xs font-semibold px-2 py-1 rounded-full bg-gray-100 text-gray-700">{{ messages.length }}</span>
      </button>

      <div v-if="expanded && messages.length" class="space-y-4">
        <article v-for="message in messages" :key="message.id" class="bg-white border border-gray-100 rounded-2xl p-5">
          <div class="flex flex-col md:flex-row md:justify-between gap-4">
            <div>
              <p class="text-gray-900 whitespace-pre-line">{{ message.content }}</p>
              <p v-if="message.comment" class="text-sm text-gray-500 mt-2">Comment: {{ message.comment }}</p>
              <a
                v-if="message.link"
                :href="message.link"
                target="_blank"
                rel="noopener noreferrer"
                class="text-sm text-indigo-600 hover:underline mt-1 inline-block"
              >Open message</a>
            </div>
            <div class="text-sm text-gray-500 md:text-right space-y-1">
              <p><strong>Platform:</strong> {{ message.platform || "—" }}</p>
              <p><strong>Category:</strong> {{ message.category || "—" }}</p>
              <p><strong>Sent:</strong> {{ formatDate(message.sent_at || message.created_at) }}</p>
            </div>
          </div>
          <div v-if="message.criteria?.length" class="mt-4 overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50">
                <tr>
                  <th class="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Criterion</th>
                  <th class="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Value</th>
                  <th class="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Comment</th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-100">
                <tr v-for="criterion in message.criteria" :key="criterion.id">
                  <td class="px-4 py-3 text-sm text-gray-800">{{ criterion.criterion_name || "–" }}</td>
                  <td class="px-4 py-3 text-sm font-semibold text-gray-900">{{ formatCriterionValue(criterion) }}</td>
                  <td class="px-4 py-3 text-sm text-gray-700">{{ criterion.comment || "—" }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p v-else class="text-sm text-gray-500 mt-3">No relevant criteria rated for this message yet.</p>
        </article>
      </div>
      <p v-else-if="expanded" class="text-sm text-gray-500">There are no reviewed messages for this user and session yet.</p>
    </template>
  </section>
</template>

<script>
import { ChevronDownIcon } from "@heroicons/vue/24/solid";
import { getAsyncStudentFeedback } from "@/api/studentFeedback";

export default {
  name: "AsyncMessagesSummary",
  components: { ChevronDownIcon },
  props: {
    userId: { type: [String, Number], required: true },
    sessionId: { type: [String, Number], required: true },
  },
  data() {
    return { expanded: true, loading: false, error: null, feedback: null, requestToken: 0 };
  },
  computed: {
    session() {
      const sessions = this.feedback?.sessions ?? [];
      return sessions.find((s) => String(s.session_id ?? s.id) === String(this.sessionId)) ?? sessions[0] ?? null;
    },
    messages() {
      const categories = Array.isArray(this.session?.categories) ? this.session.categories : [];
      const target = String(this.sessionId);
      return (this.feedback?.messages ?? []).filter((m) => {
        if (m.session_id !== null && m.session_id !== undefined) return String(m.session_id) === target;
        return !!(categories.length && m.category && categories.includes(m.category));
      });
    },
    averages() {
      const map = new Map();
      (this.feedback?.message_criteria_average ?? []).forEach((c) => {
        map.set(c.criterion_id, {
          criterion_id: c.criterion_id,
          criterion_name: c.criterion_name,
          user_average: c.average_score,
          user_ratings: c.ratings_count,
          overall_average: null,
          overall_ratings: 0,
        });
      });
      (this.feedback?.message_criteria_average_all_users ?? []).forEach((c) => {
        const entry = map.get(c.criterion_id) || {
          criterion_id: c.criterion_id,
          criterion_name: c.criterion_name,
          user_average: null,
          user_ratings: 0,
        };
        entry.overall_average = c.average_score;
        entry.overall_ratings = c.ratings_count;
        map.set(c.criterion_id, entry);
      });
      return Array.from(map.values()).sort((a, b) => (a.criterion_name || "").localeCompare(b.criterion_name || ""));
    },
  },
  watch: {
    userId: { immediate: true, handler: "load" },
    sessionId: "load",
  },
  methods: {
    async load() {
      const token = ++this.requestToken;
      if (!this.userId || !this.sessionId) {
        this.feedback = null;
        return;
      }
      this.loading = true;
      this.error = null;
      try {
        const { data } = await getAsyncStudentFeedback(this.userId, this.sessionId);
        if (token === this.requestToken) this.feedback = data;
      } catch (err) {
        if (token !== this.requestToken) return;
        this.feedback = null;
        this.error = err?.response?.data?.detail || "Unable to load rated messages.";
      } finally {
        if (token === this.requestToken) this.loading = false;
      }
    },
    formatDate(value) {
      if (!value) return "—";
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
    },
    formatAverage(value) {
      const n = Number(value);
      return value === null || value === undefined || !Number.isFinite(n) ? "—" : n.toFixed(2);
    },
    formatCriterionValue(criterion) {
      if (criterion.count_value !== null && criterion.count_value !== undefined) {
        const val = Number(criterion.count_value);
        return val > 0 ? `+${val}` : `${val}`;
      }
      if (criterion.is_fulfilled !== null && criterion.is_fulfilled !== undefined) {
        return criterion.is_fulfilled ? "Met" : "Not met";
      }
      return criterion.text_value || "—";
    },
  },
};
</script>
